import { ReactNode, useEffect } from 'react';

import { Box, CircularProgress, Typography } from '@material-ui/core';
import InfiniteScrollComponent from 'react-infinite-scroll-component';

import EmptyState from 'components/EmptyState';

type InfiniteScrollProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  next: () => void;
  hasMore: boolean;
  inverse?: boolean;
  scrollableTarget?: ReactNode | string;
  children: ReactNode;
  loading?: boolean;
  endMessage?: string;
  empty?: {
    title?: string;
    description?: string;
    icon?: ReactNode;
  };
};

export default function InfiniteScroll(props: InfiniteScrollProps) {
  const {
    data = [],
    next,
    hasMore,
    inverse,
    scrollableTarget,
    children,
    loading,
    endMessage,
    empty,
  } = props;

  // Validação para carregar mais itens caso não haja scroll na tela
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (hasMore && !loading && data.length > 0) {
      timer = setTimeout(() => {
        const target =
          typeof scrollableTarget === 'string'
            ? document.getElementById(scrollableTarget)
            : (scrollableTarget as HTMLElement);

        if (target) {
          const isScrollable = target.scrollHeight > target.clientHeight;

          if (!isScrollable) {
            next();
          }
        }
      }, 500);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [data.length, hasMore, loading, next, scrollableTarget]);

  return (
    <InfiniteScrollComponent
      dataLength={data.length} // This is important field to render the next data
      next={next}
      hasMore={hasMore}
      inverse={inverse}
      style={{
        display: 'flex',
        flexDirection: inverse ? 'column-reverse' : 'column',
      }}
      loader={<Loader />}
      scrollableTarget={scrollableTarget}
      endMessage={
        !loading && (
          <Box pt={2} pb={2} textAlign="center">
            <Typography color="textSecondary" variant="caption">
              {endMessage}
            </Typography>
          </Box>
        )
      }
    >
      {children}

      {/* initial loader */}
      {data.length <= 0 && loading && <Loader />}

      {/* empty content */}
      {data.length <= 0 && !loading && empty && (
        <EmptyState
          icon={empty?.icon}
          title={empty?.title}
          description={empty?.description}
        />
      )}
    </InfiniteScrollComponent>
  );
}

const Loader = () => (
  <Box p={2} textAlign="center">
    <CircularProgress color="inherit" size={24} />
  </Box>
);
